"use client";
import Image from "next/image";

import { useState } from "react";
import Link from "next/link";
import CalculatorShell from "@/components/CalculatorShell";
import CitationSection from "@/components/CitationSection";
import InputField from "@/components/InputField";
import SelectField from "@/components/SelectField";
import CheckboxField from "@/components/CheckboxField";
import ResultCard from "@/components/ResultCard";
import { calcDrywall, type DrywallResult } from "@/lib/formulas";
import { CALCULATOR_SPECS } from "@/lib/calculator-info-data";
import FaqSection from "@/components/FaqSection";
import RelatedCalculators from "@/components/RelatedCalculators";

const SHEET_SIZE_OPTIONS = [
  { value: "4x8", label: '4×8 ft sheet (32 sq ft) — standard' },
  { value: "4x12", label: '4×12 ft sheet (48 sq ft) — fewer joints' },
];

const THICKNESS_OPTIONS = [
  { value: "1/2", label: '½ inch — standard walls & ceilings' },
  { value: "5/8", label: '⅝ inch — fire-rated / commercial' },
];

export default function CalculatorClient() {
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [heightFt, setHeightFt] = useState("8");
  const [sheetSize, setSheetSize] = useState<"4x8" | "4x12">("4x8");
  const [thickness, setThickness] = useState("1/2");
  const [includeCeiling, setIncludeCeiling] = useState(true);
  const [result, setResult] = useState<DrywallResult | null>(null);

  function handleCalculate() {
    const res = calcDrywall({
      lengthFt: parseFloat(length) || 0,
      widthFt: parseFloat(width) || 0,
      heightFt: parseFloat(heightFt) || 8,
      includeCeiling,
      sheetSize,
    });
    setResult(res);
  }

  return (
    <CalculatorShell
      name="Drywall Calculator"
      description="Calculate how many drywall sheets you need for walls and ceilings, with screws and tape estimates included."
      icon="drywall"
      category="diy"
      accentColor="#94a3b8"
      hasResult={result !== null}
      onCalculate={handleCalculate}
      disclaimer="Out-of-plumb walls or complex cut patterns may require more sheets. Small doors/windows are not subtracted to cover waste from cut-outs."
      proTip="Order 1 extra sheet for mistake cuts. Store sheets flat in a dry area to prevent bowing before installation."
      inputsPanel={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InputField id="drywall-length" label="Room Length" unit="ft" type="number" min="0" step="0.5" placeholder="15" value={length} onChange={(e) => { setLength(e.target.value); setResult(null); }} />
            <InputField id="drywall-width" label="Room Width" unit="ft" type="number" min="0" step="0.5" placeholder="12" value={width} onChange={(e) => { setWidth(e.target.value); setResult(null); }} />
          </div>
          <InputField id="drywall-height" label="Wall Height" unit="ft" type="number" min="0" step="0.5" placeholder="8" value={heightFt} onChange={(e) => { setHeightFt(e.target.value); setResult(null); }} hint="Standard residential ceiling: 8 ft · Vaulted ceilings: 9–10 ft+" />
          <SelectField id="sheet-size" label="Sheet Size" value={sheetSize} onChange={(e) => { setSheetSize(e.target.value as "4x8" | "4x12"); setResult(null); }} options={SHEET_SIZE_OPTIONS} hint="4×12 sheets reduce seams on tall walls" />
          <SelectField id="thickness" label="Thickness" value={thickness} onChange={(e) => { setThickness(e.target.value); setResult(null); }} options={THICKNESS_OPTIONS} />
          <CheckboxField id="include-ceiling" label="Include ceiling drywall" hint="Adds room length × width to the total" checked={includeCeiling} onChange={(v) => { setIncludeCeiling(v); setResult(null); }} />
        </div>
      }
      resultsPanel={
        result && (
          <div className="space-y-3">
            <ResultCard label="Sheets Needed (+ 10% waste)" value={result.sheetsWithWaste} unit={sheetSize === "4x8" ? "4×8 sheets" : "4×12 sheets"} highlight size="lg" sub={`${result.sheetsNeeded} sheets exact`} />
            <div className="grid grid-cols-2 gap-3">
              <ResultCard label="Wall Area" value={result.wallAreaSqFt} unit="sq ft" accent />
              {includeCeiling && <ResultCard label="Ceiling Area" value={result.ceilingAreaSqFt} unit="sq ft" accent />}
            </div>
            <ResultCard label="Total Area" value={result.totalAreaSqFt} unit="sq ft" />

            {/* Accessories */}
            <div className="pt-4 mt-2">
              <div className="font-mono text-[9px] font-bold uppercase tracking-wider text-[var(--ink-faint)] pb-2 select-none">
                Accessories Estimate
              </div>
              <div className="flex flex-col border-b border-[var(--line)]">
                {[
                  { label: "Drywall screws", value: `~${result.screwsEstimate.toLocaleString()}`, unit: "screws" },
                  { label: "Joint tape", value: `~${result.tapeFt}`, unit: "linear ft" },
                  { label: `${thickness}" ${sheetSize} sheets`, value: `${result.sheetsWithWaste}`, unit: "sheets" },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex justify-between items-center py-2.5 px-3 border-t border-[var(--line)]"
                  >
                    <span className="text-[12.5px] font-sans font-semibold text-[var(--ink-soft)]">{row.label}</span>
                    <div className="text-right font-mono text-[13px]">
                      <span className="text-[var(--ink)] font-bold">
                        {row.value}
                      </span>
                      <span className="text-[10px] text-[var(--ink-faint)] ml-1.5 lowercase">
                        {row.unit}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center text-[var(--ink)]">
        {/* Row 1 - Left: Buying Guide */}
        <div className="space-y-4 md:order-1">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Drywall & Sheetrock Selection & Buying Guide
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Standard drywall installations rely on choosing appropriate board types and thicknesses. For most residential interior walls, <strong>{CALCULATOR_SPECS.drywall.buyingGuide.residentialThicknessStandard.value}-inch</strong> panels are the standard. In high-moisture zones like bathrooms, use water-resistant &quot;greenboard&quot; or &quot;purpleboard&quot;. Building codes generally mandate <strong>5/8-inch Type X</strong> fire-rated drywall for common walls separating a garage from living quarters. Select 4x12 sheets instead of standard 4x8 panels if you want to minimize the count of taped butt joints on large expanses.
          </p>
        </div>

        {/* Row 1 - Right: Buying Guide Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-2">
          <Image src="/images/drywall_material_guide.webp" alt="Drywall gypsum board panels stacked on wooden pallets in house framing construction site" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" priority />
        </div>

        {/* Row 2 - Left: Site Prep Image */}
        <div className="relative rounded-xl overflow-hidden border border-[var(--line)] aspect-[3/2] w-full bg-[var(--surface-muted)] md:order-3">
          <Image src="/images/drywall_site_prep.webp" alt="Close up of drywall sheet hung on wooden framing stud showing standard drywall screws evenly spaced" fill sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 576px" className="object-cover" loading="lazy" />
        </div>

        {/* Row 2 - Right: Site Prep Guide */}
        <div className="space-y-4 md:order-4">
          <h3 className="text-xl font-bold tracking-tight text-[var(--ink)]">
            Drywall & Sheetrock Substrate Prep & Installation
          </h3>
          <p className="text-sm leading-relaxed text-[var(--ink-soft)] font-sans">
            Before hanging drywall, ensure framing studs are flat, plumb, and free of protruding nails or fasteners. Follow standard trade specs by placing fasteners at a maximum spacing of <strong>{CALCULATOR_SPECS.drywall.sitePrep.screwSpacingWalls.value} inches</strong> along wall studs. Always install panels on the ceiling first, then the walls, tight against the ceiling sheets to provide edge support. Finishing drywall is categorized from <strong>{CALCULATOR_SPECS.drywall.sitePrep.drywallFinishLevels.value[1]} to {CALCULATOR_SPECS.drywall.sitePrep.drywallFinishLevels.value[5]}</strong>; a standard wall requires a Level 4 finish (three coats of joint compound, sanded smooth). Check our <Link href="/articles/how-much-paint-do-you-need-per-room/" className="text-[var(--accent)] hover:underline font-semibold">paint and room coverage guide</Link> to estimate primer and paint needs for your finished drywall surface.
          </p>
        </div>

      </div>

      <CitationSection
        standardCode={CALCULATOR_SPECS.drywall.industryStandards.fastenerStandard.standardCode}
        sourceName={CALCULATOR_SPECS.drywall.industryStandards.fastenerStandard.sourceName}
        sourceUrl={CALCULATOR_SPECS.drywall.industryStandards.fastenerStandard.sourceUrl}
        paragraphs={[
          <p key="p1">
            Drywall sheets must be attached using steel screws that meet the stringent material requirements of <b>{CALCULATOR_SPECS.drywall.industryStandards.fastenerStandard.standardCode}</b>. According to GA-216 installation guidelines, these fasteners must be spaced at intervals of no more than 12 inches on walls and ceilings to counteract the pulling force of gravity. Spacing can be extended up to 16 inches only when structural adhesive is used in combination with screws. Proper fastener spacing prevents sagging panels, screw pops (where the fastener head pushes through the drywall compound), and stress cracks along taped joints.
          </p>,
          <p key="p2">
            In addition to spacing, the depth of the screw is critical. The screw head must sink slightly below the paper face of the board to form a clean dimple, without tearing or puncturing the paper liner. If the paper is broken, the structural hold is compromised, rendering the screw useless. Builders must also follow ASTM C840 finish level requirements: standard paintable walls require a Level 4 finish (three separate coats of mud), while ceilings subject to harsh, direct sunlight or walls with high-gloss paint require a Level 5 finish, which includes a complete skim coat across the entire drywall surface.
          </p>
        ]}
        keyRequirements={[
          "Drywall screws must comply with ASTM C1002 material requirements.",
          "Fasteners must be spaced at a maximum of 12 inches on walls and ceilings.",
          "Spacing may be extended up to 16 inches only with structural adhesive.",
          "Screw heads must form a dimple without tearing the paper liner."
        ]}
      />

      <FaqSection calculatorId="drywall" />
      <RelatedCalculators calculatorId="drywall" />
    </CalculatorShell>
  );
}
